/**
 * Accessibility Hook
 * Provides comprehensive accessibility features for Team B components
 * Supports WCAG 2.1 AA compliance and enhanced user preferences
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

interface AccessibilityPreferences {
  reducedMotion: boolean;
  highContrast: boolean;
  largeText: boolean;
  screenReader: boolean;
  keyboardNavigation: boolean;
  focusVisible: boolean;
  colorBlindness: 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  prefersDarkMode: boolean;
}

interface AccessibilityState {
  preferences: AccessibilityPreferences;
  announcements: string[];
  focusHistory: string[];
  currentFocus: string | null;
}

interface AccessibilityActions {
  updatePreference: <K extends keyof AccessibilityPreferences>(
    key: K,
    value: AccessibilityPreferences[K]
  ) => void;
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  clearAnnouncements: () => void;
  setFocus: (elementId: string) => void;
  navigateToNext: () => void;
  navigateToPrevious: () => void;
  getAriaProps: (config: AriaConfig) => AriaProps;
  getColorScheme: () => ColorScheme;
}

interface AriaConfig {
  role?: string;
  label?: string;
  describedBy?: string;
  expanded?: boolean;
  hasPopup?: boolean;
  live?: 'off' | 'polite' | 'assertive';
  atomic?: boolean;
  busy?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  selected?: boolean;
  checked?: boolean;
  level?: number;
  setSize?: number;
  posInSet?: number;
}

interface AriaProps {
  role?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-live'?: 'off' | 'polite' | 'assertive';
  'aria-atomic'?: boolean;
  'aria-busy'?: boolean;
  'aria-disabled'?: boolean;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
  'aria-selected'?: boolean;
  'aria-checked'?: boolean;
  'aria-level'?: number;
  'aria-setsize'?: number;
  'aria-posinset'?: number;
  tabIndex?: number;
}

interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

const defaultPreferences: AccessibilityPreferences = {
  reducedMotion: false,
  highContrast: false,
  largeText: false,
  screenReader: false,
  keyboardNavigation: false,
  focusVisible: true,
  colorBlindness: 'none',
  prefersDarkMode: false,
};

export const useAccessibility = (): AccessibilityState & AccessibilityActions => {
  const [state, setState] = useState<AccessibilityState>({
    preferences: defaultPreferences,
    announcements: [],
    focusHistory: [],
    currentFocus: null,
  });

  // Detect system preferences on mount
  useEffect(() => {
    const detectSystemPreferences = () => {
      const updates: Partial<AccessibilityPreferences> = {};

      // Detect reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        updates.reducedMotion = true;
      }

      // Detect high contrast preference
      if (window.matchMedia('(prefers-contrast: high)').matches) {
        updates.highContrast = true;
      }

      // Detect dark mode preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        updates.prefersDarkMode = true;
      }

      // Detect screen reader usage
      if (navigator.userAgent.includes('NVDA') || 
          navigator.userAgent.includes('JAWS') || 
          navigator.userAgent.includes('VoiceOver')) {
        updates.screenReader = true;
      }

      // Check for keyboard navigation
      const hasKeyboardNavigation = !('ontouchstart' in window) && 
                                   !navigator.maxTouchPoints;
      if (hasKeyboardNavigation) {
        updates.keyboardNavigation = true;
      }

      if (Object.keys(updates).length > 0) {
        setState(prev => ({
          ...prev,
          preferences: { ...prev.preferences, ...updates },
        }));
      }
    };

    detectSystemPreferences();

    // Listen for media query changes
    const mediaQueries = [
      window.matchMedia('(prefers-reduced-motion: reduce)'),
      window.matchMedia('(prefers-contrast: high)'),
      window.matchMedia('(prefers-color-scheme: dark)'),
    ];

    const handleMediaChange = () => detectSystemPreferences();

    mediaQueries.forEach(mq => mq.addEventListener('change', handleMediaChange));

    return () => {
      mediaQueries.forEach(mq => mq.removeEventListener('change', handleMediaChange));
    };
  }, []);

  // Set up global keyboard navigation
  useEffect(() => {
    if (!state.preferences.keyboardNavigation) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Tab navigation
      if (event.key === 'Tab') {
        setState(prev => ({
          ...prev,
          focusHistory: [...prev.focusHistory, document.activeElement?.id || ''].slice(-10),
        }));
      }

      // Escape key to close modals/dialogs
      if (event.key === 'Escape') {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement?.getAttribute('role') === 'dialog') {
          activeElement.blur();
        }
      }

      // Arrow key navigation for components
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        const focusedElement = document.activeElement as HTMLElement;
        if (focusedElement?.getAttribute('data-keyboard-navigable')) {
          event.preventDefault();
          handleArrowNavigation(event.key, focusedElement);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [state.preferences.keyboardNavigation]);

  // Arrow key navigation handler
  const handleArrowNavigation = (key: string, currentElement: HTMLElement) => {
    const parent = currentElement.closest('[data-keyboard-navigation-group]');
    if (!parent) return;

    const navigableElements = Array.from(
      parent.querySelectorAll('[data-keyboard-navigable]')
    ) as HTMLElement[];

    const currentIndex = navigableElements.indexOf(currentElement);
    let nextIndex = currentIndex;

    switch (key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : navigableElements.length - 1;
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = currentIndex < navigableElements.length - 1 ? currentIndex + 1 : 0;
        break;
    }

    if (nextIndex !== currentIndex) {
      navigableElements[nextIndex]?.focus();
    }
  };

  // Update preference
  const updatePreference = useCallback(<K extends keyof AccessibilityPreferences>(
    key: K,
    value: AccessibilityPreferences[K]
  ) => {
    setState(prev => ({
      ...prev,
      preferences: { ...prev.preferences, [key]: value },
    }));

    // Save to localStorage
    const saved = JSON.parse(localStorage.getItem('accessibility-preferences') || '{}');
    localStorage.setItem('accessibility-preferences', JSON.stringify({
      ...saved,
      [key]: value,
    }));
  }, []);

  // Announce message to screen readers
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    setState(prev => ({
      ...prev,
      announcements: [...prev.announcements, message],
    }));

    // Create or update live region
    let liveRegion = document.getElementById(`aria-live-${priority}`);
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = `aria-live-${priority}`;
      liveRegion.setAttribute('aria-live', priority);
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      liveRegion!.textContent = '';
    }, 1000);
  }, []);

  // Clear announcements
  const clearAnnouncements = useCallback(() => {
    setState(prev => ({ ...prev, announcements: [] }));
  }, []);

  // Set focus to element
  const setFocus = useCallback((elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.focus();
      setState(prev => ({
        ...prev,
        currentFocus: elementId,
        focusHistory: [...prev.focusHistory, elementId].slice(-10),
      }));
    }
  }, []);

  // Navigate to next focusable element
  const navigateToNext = useCallback(() => {
    const focusableElements = Array.from(
      document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ) as HTMLElement[];

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    focusableElements[nextIndex]?.focus();
  }, []);

  // Navigate to previous focusable element
  const navigateToPrevious = useCallback(() => {
    const focusableElements = Array.from(
      document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ) as HTMLElement[];

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
    focusableElements[prevIndex]?.focus();
  }, []);

  // Generate ARIA props
  const getAriaProps = useCallback((config: AriaConfig): AriaProps => {
    const props: AriaProps = {};

    if (config.role) props.role = config.role;
    if (config.label) props['aria-label'] = config.label;
    if (config.describedBy) props['aria-describedby'] = config.describedBy;
    if (config.expanded !== undefined) props['aria-expanded'] = config.expanded;
    if (config.hasPopup !== undefined) props['aria-haspopup'] = config.hasPopup;
    if (config.live) props['aria-live'] = config.live;
    if (config.atomic !== undefined) props['aria-atomic'] = config.atomic;
    if (config.busy !== undefined) props['aria-busy'] = config.busy;
    if (config.disabled !== undefined) props['aria-disabled'] = config.disabled;
    if (config.invalid !== undefined) props['aria-invalid'] = config.invalid;
    if (config.required !== undefined) props['aria-required'] = config.required;
    if (config.selected !== undefined) props['aria-selected'] = config.selected;
    if (config.checked !== undefined) props['aria-checked'] = config.checked;
    if (config.level !== undefined) props['aria-level'] = config.level;
    if (config.setSize !== undefined) props['aria-setsize'] = config.setSize;
    if (config.posInSet !== undefined) props['aria-posinset'] = config.posInSet;

    // Add keyboard navigation support
    if (state.preferences.keyboardNavigation) {
      props.tabIndex = 0;
    }

    return props;
  }, [state.preferences.keyboardNavigation]);

  // Get color scheme based on preferences
  const getColorScheme = useCallback((): ColorScheme => {
    const { highContrast, prefersDarkMode, colorBlindness } = state.preferences;

    let scheme: ColorScheme;

    if (highContrast) {
      scheme = {
        primary: '#000000',
        secondary: '#333333',
        accent: '#0066CC',
        background: '#FFFFFF',
        surface: '#F5F5F5',
        text: '#000000',
        textSecondary: '#333333',
        border: '#000000',
        success: '#006600',
        warning: '#FF6600',
        error: '#CC0000',
        info: '#0066CC',
      };
    } else if (prefersDarkMode) {
      scheme = {
        primary: '#1976D2',
        secondary: '#424242',
        accent: '#FF4081',
        background: '#121212',
        surface: '#1E1E1E',
        text: '#FFFFFF',
        textSecondary: '#CCCCCC',
        border: '#333333',
        success: '#4CAF50',
        warning: '#FF9800',
        error: '#F44336',
        info: '#2196F3',
      };
    } else {
      scheme = {
        primary: '#1976D2',
        secondary: '#757575',
        accent: '#FF4081',
        background: '#FFFFFF',
        surface: '#F5F5F5',
        text: '#212121',
        textSecondary: '#757575',
        border: '#E0E0E0',
        success: '#4CAF50',
        warning: '#FF9800',
        error: '#F44336',
        info: '#2196F3',
      };
    }

    // Adjust for color blindness
    if (colorBlindness !== 'none') {
      scheme = adjustForColorBlindness(scheme, colorBlindness);
    }

    return scheme;
  }, [state.preferences]);

  return {
    ...state,
    updatePreference,
    announce,
    clearAnnouncements,
    setFocus,
    navigateToNext,
    navigateToPrevious,
    getAriaProps,
    getColorScheme,
  };
};

// Adjust colors for color blindness
const adjustForColorBlindness = (
  scheme: ColorScheme,
  type: 'protanopia' | 'deuteranopia' | 'tritanopia'
): ColorScheme => {
  const adjustments = {
    protanopia: {
      // Red-blind: use blue/yellow instead of red/green
      success: '#0066CC',
      error: '#FF6600',
    },
    deuteranopia: {
      // Green-blind: use blue/yellow instead of red/green
      success: '#0066CC',
      error: '#FF6600',
    },
    tritanopia: {
      // Blue-blind: use red/green more distinctly
      info: '#FF6600',
      primary: '#006600',
    },
  };

  return { ...scheme, ...adjustments[type] };
};

// Hook for specific accessibility features
export const useScreenReader = () => {
  const { preferences, announce } = useAccessibility();
  return {
    isScreenReader: preferences.screenReader,
    announce,
  };
};

export const useKeyboardNavigation = () => {
  const { preferences, setFocus, navigateToNext, navigateToPrevious } = useAccessibility();
  return {
    isKeyboardNavigation: preferences.keyboardNavigation,
    setFocus,
    navigateToNext,
    navigateToPrevious,
  };
};

export const useReducedMotion = () => {
  const { preferences } = useAccessibility();
  return preferences.reducedMotion;
};